import { useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  useAgentProfileCreateControllerAgentProfileCreateForUsersMutation,
  useAgentProfileEditControllerAgentProfileEditForUsersMutation,
  type AgentProfileResponseDto,
  type TaskFlowDto,
} from '@shared/services/apiService';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import { copyJobWorkflow } from './copyJobWorkflow';
import { jobMetadata, type WorkspaceJobProfile } from './jobMetadata';

/** Native create cannot carry workflow data; retain the created ID when the second save fails. */
export const CopyJobAction = ({
  profile,
  disabled,
  loadCurrent,
  onCopied,
}: {
  profile: WorkspaceJobProfile;
  disabled: boolean;
  loadCurrent: () => Promise<WorkspaceJobProfile | undefined>;
  onCopied: (profile: AgentProfileResponseDto) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(`${profile.name} copy`);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<AgentProfileResponseDto | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const workflowCopy = useRef<TaskFlowDto | null>(null);
  const [create, creating] = useAgentProfileCreateControllerAgentProfileCreateForUsersMutation();
  const [edit, editing] = useAgentProfileEditControllerAgentProfileEditForUsersMutation();
  const busy = useRef(false);
  const readable = profile.type !== 'system' && typeof profile.prompt === 'string';
  const active = useRef(true);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const copy = async () => {
    if (
      busy.current ||
      disabled ||
      !readable ||
      uncertain ||
      !name.trim() ||
      name.trim().length > 120
    )
      return;
    busy.current = true;
    setPreparing(true);
    setError('');
    try {
      const current = await loadCurrent();
      if (!active.current || disabledRef.current) return;
      if (
        !current ||
        current.type === 'system' ||
        typeof current.prompt !== 'string' ||
        !isEqual(current, profile)
      )
        throw new Error('The source job changed or is unavailable. Reload before copying.');
      let result = created;
      if (!result) {
        workflowCopy.current = current.taskFlow ? copyJobWorkflow(current.taskFlow) : null;
        try {
          result = await create({
            agentProfileCreateRequestDto: {
              name: name.trim(),
              prompt: current.prompt || '',
              firstMessage: current.firstMessage,
              firstMessageInterruptionsEnabled: current.firstMessageInterruptionsEnabled,
              ...jobMetadata(current),
            },
          }).unwrap();
        } catch (e) {
          setUncertain(true);
          throw e;
        }
        if (!active.current) return;
        setCreated(result);
        if (disabledRef.current) return;
      }
      if (workflowCopy.current)
        result = await edit({
          profileId: result.id,
          agentProfileEditRequestDto: { taskFlow: workflowCopy.current },
        }).unwrap();
      if (!active.current || disabledRef.current) return;
      setOpen(false);
      setCreated(null);
      workflowCopy.current = null;
      setName(`${profile.name} copy`);
      onCopied(result);
    } catch (e) {
      if (active.current)
        setError(
          getApiErrorMessage(e, e instanceof Error ? e.message : 'Unable to copy this job.'),
        );
    } finally {
      busy.current = false;
      if (active.current) setPreparing(false);
    }
  };
  const loading = preparing || creating.isLoading || editing.isLoading;
  return (
    <>
      <Button variant="outlined" disabled={disabled || !readable} onClick={() => setOpen(true)}>
        Create editable copy
      </Button>
      <Dialog
        open={open}
        onClose={() => {
          if (!loading) setOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        aria-labelledby="copy-job-title">
        <DialogTitle id="copy-job-title">Create an editable job copy</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 1 }}>
            <Typography>
              Copy the instructions, opening line and complete workflow. The source job stays
              unchanged. Selecting the copy for an agent requires saving its settings.
            </Typography>
            <TextField
              label="Copy name"
              value={name}
              disabled={loading || !!created || uncertain}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ maxLength: 120 }}
            />
            {error && <Alert severity="error">{error}</Alert>}
            {created && (
              <Alert severity="info">
                The custom job exists. Retry workflow copying on this same record; another job will
                not be created.
              </Alert>
            )}
            {uncertain && (
              <Alert severity="warning">
                Creation was not confirmed. Review the current Job library before creating another
                copy.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={() => setOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            disabled={disabled || !readable || loading || uncertain || !name.trim()}
            onClick={() => void copy()}>
            {created ? 'Retry workflow copy' : 'Confirm copy'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
